import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActivitiesEditForm from '../../forms/ActivitiesEditForm';
import { ROUTES } from '../../constants/routes';
import { DATETIME_FORMAT, DEFAULT_DATE_FORMAT, GET_ALL_QUERY_PARAMS } from '../../constants';
import { getActivitiesList, getCustomActivities, getTasksheetPermitActivitiesForUser } from '../../api/activities';
import { getStripeInvoicesList } from '../../api/invoices';
import formatDate, { formatDateTime } from '../../utils/formatDate';
import useTablePage from '../../hooks/useTablePage';
import { getTickets } from '../../api/tickets';
import TicketsEditForm from '../../forms/TicketsEditForm';
import DocumentsEditForm from '../../forms/DocumentsEditForm';
import ConditionsEditForm from '../../forms/ConditionsEditForm';
import { RootState } from '../../redux/root';
import { setUseTaskSheet } from '../../redux/slices/allActivitiesSlice';
import ReviewsTable from './SheetBlockTables/ReviewsTable';
import { PM_PT_TITLE } from '../../utils/controlsDisableUtil';
import DocumentsTable from './SheetBlockTables/DocumentsTable';
import TicketsTable from './SheetBlockTables/TicketsTable';
import ConditionsTable from './SheetBlockTables/ConditionsTable';
import ActivitiesTable from './SheetBlockTables/ActivitiesTable';

const labels = {
  invoice: 'invoice',
  documents: 'documents',
  tickets: 'tickets',
  waitingStatus: 45,
  underReviewStatus: 47,
  completedStatus: 42,
};

const TaskSheetBlock = () => {
  const { push }: History = useHistory();

  const [invoicesColumn, setInvoicesColumn] = useState(0);
  const [ticketsColumn, setTicketsColumn] = useState(0);
  const [documentsColumn, setDocumentsColumn] = useState(0);
  const [reviewsColumn, setReviewsColumn] = useState(0);
  const [conditionsColumn, setConditionsColumn] = useState(0);
  const pageSize = useSelector((s:RootState) => s.pagination.pageSize);
  const { id: currentUserId, selectedFranchise } = useSelector((s:RootState) => s.userAuth.info);
  const [editFormData, setEditFormData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTicketsTable, setShowTicketsTable] = useState(false);
  const [showDocumentsTable, setShowDocumentsTable] = useState(false);
  const [showConditionsTable, setShowConditionsTable] = useState(false);
  const [showReviewsTable, setShowReviewsTable] = useState(false);
  const [documentRows, setDocumentRows] = useState<any[]>([]);
  const [ticketsRows, setTicketsRows] = useState<any[]>([]);
  const [conditionsRows, setConditionsRows] = useState<any[]>([]);
  const [activitiesRows, setActivitiesRows] = useState<any[]>([]);
  const [reviewsRows, setReviewsRows] = useState<any[]>([]);
  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false);
  const { updateTable, tableManuallyUpdate } = useTablePage({});
  const dispatch = useDispatch();
  React.useEffect(() => {
    const today = formatDate(new Date(), DEFAULT_DATE_FORMAT);
    const loadData = async () => {
      // Invoices
      const invoicesResponse = await getStripeInvoicesList({
        createdBefore: today,
        franchise_id: selectedFranchise,
      });
      setInvoicesColumn(invoicesResponse.data.count);

      // Tickets
      const ticketResponse = await getTickets({
        assignedTo: currentUserId,
        franchise_id: selectedFranchise,
      });
      setTicketsColumn(ticketResponse?.data?.count);
      setTicketsRows(ticketResponse?.data?.results);

      // Documents
      const docResponse = await getModifiedDocuments({
        is_deleted: false,
        limit: pageSize,
        event_type: 'Activity',
        activity_type_name: 'Document',
        task_sheet: true,
        franchise_id: selectedFranchise,
      });
      setDocumentsColumn(docResponse?.data?.highestCount);
      setDocumentRows(docResponse?.data?.results);

      // Reviews
      const reviewResponse = await getCustomActivities({
        is_deleted: false,
        limit: pageSize,
        event_type: 'Activity',
        activity_type_name: 'Reviews',
        task_sheet: true,
        franchise_id: selectedFranchise,
      });
      const _revRows = reviewResponse?.data?.results?.filter(r => r.activityTitleName === PM_PT_TITLE) || [];
      setReviewsColumn(_revRows.length);
      setReviewsRows(_revRows);

      // Conditions
      const conditionResponse = await getCustomActivities({
        is_deleted: false,
        limit: pageSize,
        event_type: 'Activity',
        activity_type_name: 'Conditions',
        franchise_id: selectedFranchise,
        task_sheet: true,
      });
      setConditionsColumn(conditionResponse?.data?.highestCount);
      setConditionsRows(conditionResponse?.data?.results);
    };
     getModifiedActivities({});
    loadData();
  }, [selectedFranchise, currentUserId]);

  const getModifiedDocuments = params =>
    getCustomActivities({
      ...params,
      event_type: 'Activity',
      activity_type_name: 'Document',
      task_sheet: true,
      franchise_id: selectedFranchise,
    }).then(response => {
      const groupedArr = response.data.results.reduce((itemArr, item) => {
        const key = `${item.projectId}-${item.permitId}`;
        let _item = itemArr[key];
        if (!_item) {
           _item = { entries: [] };
           _item.entries.push(item);
           _item.permitStatus = item.permitStatus;
           _item.name = `${item.projectName}-${item.permitName}`;
           _item.projectId = item.projectId;
           _item.permitId = item.permitId;
           itemArr[key] = _item;
        } else {
          _item.entries.push(item);
        }
        return itemArr;
      }, {});

      const count = Object.keys(groupedArr).length;
      return {
        ...response,
        data: {
          ...response.data,
          highestCount: count,
          overallTotal: count,
          results: Object.keys(groupedArr).map(key => {
            const item = groupedArr[key];
            return {
              projectPermit: `${item?.name}`,
              permitStatus: item?.permitStatus,
              quantity: item.entries.length,
              projectId: item.projectId,
              permitId: item.permitId,
            };
          }),
        },
      };
    });

  const getModifiedActivities = params =>
    Promise.all([
      getTasksheetPermitActivitiesForUser({
        ...params,
        assignedTo: currentUserId,
      }),
      getActivitiesList({
        ...GET_ALL_QUERY_PARAMS,
      }),
    ])
      .then(([activitiesResponse, activityTitlesResponse]) => {
        const rowsData = {
          ...activitiesResponse,
          data: {
            ...activitiesResponse?.data,
            results: activitiesResponse?.data?.data.map(el => {
              const activitiesTitles = activityTitlesResponse.data.results?.filter(item => item?.isActiveStatus === 'True');
              const title = activitiesTitles.find(item => item?.id === el.title);

              return {
                ...el,
                activityTitle: title?.name,
                projectPermit: `${el?.projectName} - ${el?.permitName}`,
                followUp: formatDateTime(el.followUp, DATETIME_FORMAT),
              };
            }),
          },
        };
        setActivitiesRows(rowsData.data.results);
      })
      .catch(err => console.error(err))
      .finally(() => {});

  const onChangeRoute = route => {
    if (route === labels.invoice) {
      dispatch(setUseTaskSheet(true));
      push(`${ROUTES.ALL_ACTIVITIES}/fee`, { from: 'tasksheet' });
    } else {
      push(`${ROUTES.ALL_ACTIVITIES}/${route}`);
    }
  };

  const handleCloseInfoModal = closeInfo => {
    setEditFormData(null);
    updateTable();
    closeInfo();
  };

  return (
    <Wrapper>
      <Header>Task Sheet</Header>
      <Content>
        <HeaderBlock>
          <StyledParagraph onClick={() => onChangeRoute(labels.invoice)}>
            Invoices(Click here): <b>{invoicesColumn}</b>
          </StyledParagraph>
          <StyledParagraph
            onClick={() => {
              setShowTicketsTable(true);
              setShowDocumentsTable(false);
              setShowReviewsTable(false);
            }}
          >
            Tickets: <b>{ticketsColumn}</b>
          </StyledParagraph>
          <StyledParagraph
            onClick={() => {
              setShowTicketsTable(false);
              setShowDocumentsTable(true);
              setShowReviewsTable(false);
            }}
          >
            Documents: <b>{documentsColumn}</b>
          </StyledParagraph>
          <StyledParagraph onClick={() => {
             setShowTicketsTable(false);
             setShowDocumentsTable(false);
             setShowConditionsTable(false);
             setShowReviewsTable(true);
          }}
          >
            Reviews: <b>{reviewsColumn}</b>
          </StyledParagraph>
          <StyledParagraph
            onClick={() => {
              setShowTicketsTable(false);
              setShowDocumentsTable(false);
              setShowConditionsTable(true);
              setShowReviewsTable(false);
            }}
          >
            Conditions: <b>{conditionsColumn}</b>
          </StyledParagraph>
        </HeaderBlock>
        <StyledTitle>
          {showTicketsTable ? 'Tickets' : showDocumentsTable ? 'Documents' : showConditionsTable ? 'Conditions' : showReviewsTable ? 'Reviews' : 'Activities'}
        </StyledTitle>
        {showTicketsTable && (
          <TicketsTable TicketsRows={ticketsRows} />
        )}

        {showDocumentsTable && (
          <DocumentsTable DocumentsRows={documentRows} />
        )}

        {showReviewsTable && (
        <ReviewsTable reviewsRows={reviewsRows} />
       )}
        {showConditionsTable && (
          <ConditionsTable ConditionsRows={conditionsRows} />
        )}
        {currentUserId && !showTicketsTable && !showDocumentsTable && !showConditionsTable && !showReviewsTable && (
          <ActivitiesTable ActivitiesRows={activitiesRows} />
        )}
        {isModalOpen && editFormData && (
          <ActivitiesEditForm
            initialValues={editFormData}
            handleCloseInfoModal={handleCloseInfoModal}
            permit={editFormData.permit}
            setInitialValues={setEditFormData}
          />
        )}
        {isTicketsModalOpen && editFormData && (
          <TicketsEditForm
            initialValues={editFormData}
            handleCloseInfoModal={handleCloseInfoModal}
            permit={editFormData.permit}
            project={editFormData.projectId}
            setInitialValues={setEditFormData}
          />
        )}
        {isDocumentsModalOpen && editFormData && (
          <DocumentsEditForm
            initialValues={editFormData}
            handleCloseInfoModal={handleCloseInfoModal}
            permit={editFormData.permitId}
            project={editFormData.projectId}
            setInitialValues={setEditFormData}
          />
        )}
        {isConditionsModalOpen && editFormData && (
          <ConditionsEditForm
            initialValues={editFormData}
            handleCloseInfoModal={handleCloseInfoModal}
            permit={editFormData.permitId}
            project={editFormData.projectId}
            setInitialValues={setEditFormData}
          />
        )}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex: 1;
  margin-right: 10px;
  padding: 6px;
  width: 760px;
  min-width: 0;
  min-height: 300px;
  border: 1px solid var(--border);
  border-radius: 5px;
  overflow: auto;
  box-sizing: border-box;

  @media (max-width: 840px) {
    width: 100%;
    margin-right: 0;
  }
`;
const Header = styled.div`
  background: var(--primary);
  color: var(--defaultBackground);
  text-align: center;
  border-radius: 5px;
  padding: 8px 0;
`;
const Content = styled.div`
  overflow: auto;
  height: 290px;
`;
const HeaderBlock = styled.div`
  display: flex;
  justify-content: space-between;
`;
const StyledParagraph = styled.p`
  font-size: 14px;
  cursor: pointer;
  b {
    color: var(--primary);
  }
`;
const StyledTitle = styled.h4`
  text-align: center;
  margin: 6px 0;
`;

export default TaskSheetBlock;
